import { Workbook } from "@progress/kendo-ooxml";
import { saveAs } from "@progress/kendo-file-saver";

// Excel export for the Parent Renewal Dashboard, matching the layout the business
// supplied in client_status_by_parent.xlsx.
//
// Shape:
//   row 1        headers
//   row 2        All Clients (N parents)  -- every client, parents and children
//   row 3        All Parents (N)          -- parent companies only
//   row 4..      one row per parent, each followed by its children at outline
//                level 1, so Excel renders a collapsible +/- for the group
//
// Parent rows carry the group's tally; child rows leave those columns blank and
// carry only their own identity, people and status. That is how the supplied file
// reads, and it keeps a child from looking like a group of one.

export const EXPORT_COLUMNS = [
  "Parent Id",
  "Parent Name",
  "Clients in Group",
  "Children",
  "Not Started",
  "In Progress",
  "Submitted for Review",
  "Published",
  "Published Default",
  "Publish In Progress",
  "Publish Error",
  "% Complete",
  "Broker",
  "Benefit Representative",
  "Sales Representative",
  "Business Development Manager",
  "Benefits Account Manager",
  "Status",
  "Submission Record",
];

// One bucket per status column, in column order.
const BUCKETS = [
  "notStarted",
  "inProgress",
  "submitted",
  "published",
  "publishedDefault",
  "publishInProgress",
  "publishError",
];

const bucketFor = (oeStatus) => {
  switch (oeStatus) {
    case "NotStarted":
      return "notStarted";
    case "Submitted":
      return "submitted";
    case "Published":
      return "published";
    case "PublishedDefault":
      return "publishedDefault";
    case "PublishInProgress":
      return "publishInProgress";
    case "PublishError":
      return "publishError";
    case "InProgress":
    default:
      return "inProgress";
  }
};

const emptyTally = () => BUCKETS.reduce((acc, b) => ({ ...acc, [b]: 0 }), {});

const addToTally = (tally, oeStatus) => {
  if (!oeStatus) return tally;
  tally[bucketFor(oeStatus)] += 1;
  return tally;
};

// Published and Published Default both mean the client is done.
const percentComplete = (tally, total) =>
  total > 0 ? (tally.published + tally.publishedDefault) / total : 0;

const tallyCells = (tally) => BUCKETS.map((b) => ({ value: tally[b] }));

const peopleCells = (row) => [
  { value: row.broker || "" },
  { value: row.benefitRepName || "" },
  { value: row.salesRepName || "" },
  { value: row.bdmName || "" },
  { value: row.bamName || "" },
  { value: row.oeStatusDesc || "" },
  { value: row.submissionRecord || "" },
];

/**
 * Turns the dashboard's grouped payload into the workbook rows.
 * Exported separately from the save so it can be tested without a browser.
 */
export const buildExportRows = (groups) => {
  const ordered = [...(groups || [])].sort((a, b) =>
    (a.clientName || "").localeCompare(b.clientName || ""),
  );

  const allClients = emptyTally();
  const parentsOnly = emptyTally();
  let totalClients = 0;
  let totalChildren = 0;

  const bodyRows = [];

  ordered.forEach((group) => {
    const children = group.subClients || [];
    // A synthesized "not in season" member -- the parent or any pass-through link in
    // the chain -- has no OE work of its own. It is not a real, assessable client, just
    // a label for where it sits in the hierarchy, so it is excluded from every tally
    // and count column, though its row still appears for visibility.
    const realChildren = children.filter((child) => !child.notInSeason);
    const clientsInGroup = (group.notInSeason ? 0 : 1) + realChildren.length;

    totalClients += clientsInGroup;
    totalChildren += realChildren.length;

    const groupTally = emptyTally();
    if (!group.notInSeason) {
      addToTally(groupTally, group.oeStatus);
      addToTally(allClients, group.oeStatus);
      addToTally(parentsOnly, group.oeStatus);
    }
    realChildren.forEach((child) => addToTally(groupTally, child.oeStatus));
    realChildren.forEach((child) => addToTally(allClients, child.oeStatus));

    bodyRows.push({
      cells: [
        { value: group.clientId },
        { value: group.clientName },
        { value: clientsInGroup },
        { value: children.length },
        ...tallyCells(groupTally),
        { value: percentComplete(groupTally, clientsInGroup), format: "0.00%" },
        ...peopleCells(group),
      ],
    });

    children.forEach((child) => {
      bodyRows.push({
        // level 1 is what makes Excel draw the expander against the parent above.
        level: 1,
        cells: [
          { value: child.clientId },
          { value: child.clientName },
          // Tally columns stay blank on a child: it is a member of a group, not a
          // group of its own.
          // Distinct objects, not one shared reference repeated ten times: the
          // workbook writer decorates cells in place.
          ...Array.from({ length: 10 }, () => ({ value: null })),
          ...peopleCells(child),
        ],
      });
    });
  });

  const parentCount = ordered.length;

  const summaryRows = [
    {
      cells: [
        { value: null },
        { value: `All Clients (${parentCount} parents)`, bold: true },
        { value: totalClients, bold: true },
        { value: totalChildren, bold: true },
        ...tallyCells(allClients).map((c) => ({ ...c, bold: true })),
        {
          value: percentComplete(allClients, totalClients),
          format: "0.00%",
          bold: true,
        },
      ],
    },
    {
      cells: [
        { value: null },
        { value: `All Parents (${parentCount})`, bold: true },
        { value: parentCount, bold: true },
        // Children is deliberately blank here: a parent-only view has none.
        { value: null },
        ...tallyCells(parentsOnly).map((c) => ({ ...c, bold: true })),
        {
          value: percentComplete(parentsOnly, parentCount),
          format: "0.00%",
          bold: true,
        },
      ],
    },
  ];

  return { summaryRows, bodyRows, totals: { allClients, parentsOnly, totalClients, totalChildren, parentCount } };
};

export const buildWorkbook = (groups, sheetName = "By Parent") => {
  const { summaryRows, bodyRows } = buildExportRows(groups);

  return new Workbook({
    sheets: [
      {
        name: sheetName,
        freezePane: { rowSplit: 3 },
        columns: [
          { width: 90 },
          { width: 260 },
          { width: 110 },
          { width: 80 },
          { width: 90 },
          { width: 90 },
          { width: 140 },
          { width: 90 },
          { width: 120 },
          { width: 130 },
          { width: 100 },
          { width: 90 },
          { width: 160 },
          { width: 170 },
          { width: 160 },
          { width: 200 },
          { width: 190 },
          { width: 130 },
          { width: 170 },
        ],
        rows: [
          { cells: EXPORT_COLUMNS.map((title) => ({ value: title, bold: true })) },
          ...summaryRows,
          ...bodyRows,
        ],
      },
    ],
  });
};

export const saveParentRenewalWorkbook = async (groups, fileName) => {
  const dataUrl = await buildWorkbook(groups).toDataURL();
  saveAs(dataUrl, fileName);
};
